import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook for managing a user's music library (liked tracks)
 * Uses kind 30078 (Application-specific data) with d-tag "cove-library"
 */
export function useLibrary() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const { mutateAsync: publishEvent } = useNostrPublish();

  // Query the user's library
  const { data: libraryEvent, isLoading } = useQuery({
    queryKey: ['library', user?.pubkey],
    queryFn: async () => {
      if (!user?.pubkey) return null;
      
      const events = await nostr.query([{
        kinds: [30078],
        authors: [user.pubkey],
        '#d': ['cove-library'],
        limit: 1,
      }]);

      return events[0] || null;
    },
    enabled: !!user?.pubkey,
    staleTime: 1000 * 60, // 1 minute
  });

  // Parse liked track IDs from the library event
  const likedTrackIds: string[] = libraryEvent?.tags
    .filter(([name]) => name === 'e')
    .map(([_, id]) => id) || [];

  // Check if a track is liked
  const isLiked = (trackId: string): boolean => {
    return likedTrackIds.includes(trackId);
  };

  // Toggle like status
  const toggleLike = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) throw new Error('Must be logged in');

      const currentIds = [...likedTrackIds];
      const isCurrentlyLiked = currentIds.includes(trackId);
      
      let newIds: string[];
      if (isCurrentlyLiked) {
        newIds = currentIds.filter(id => id !== trackId);
      } else {
        newIds = [trackId, ...currentIds];
      }

      // Create new library event
      const tags: string[][] = [
        ['d', 'cove-library'],
        ...newIds.map(id => ['e', id]),
      ];

      await publishEvent({
        kind: 30078,
        content: '',
        tags,
      });

      return !isCurrentlyLiked;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library', user?.pubkey] });
    },
  });

  return {
    likedTrackIds,
    isLiked,
    toggleLike: toggleLike.mutate,
    toggleLikeAsync: toggleLike.mutateAsync,
    isLoading,
    isToggling: toggleLike.isPending,
  };
}
